import React, { useEffect, useState } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { oapi, fmtMoney, minorToInput } from '@/lib/officialsApi';
import { SettingsCard, useSaver } from '../SettingsCard';
import { Field } from './PayBits';

const SaveBtn = ({ busy, onClick, testId }) => (
  <div className="flex justify-end mt-4"><Button size="sm" disabled={busy} onClick={onClick} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid={testId}>
    {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save</Button></div>
);

const NOTIFY = [['approved', 'Email officials when their earnings are approved'], ['paid', 'Email officials when a payment is recorded'], ['statement', 'Allow emailing payment statements']];

const GeneralCard = ({ p, currencies, can, save }) => {
  const [cur, setCur] = useState(p.currency);
  const [notify, setNotify] = useState(p.notify);
  const [busy, run] = useSaver(save);
  return (
    <SettingsCard title="Currency & notifications" desc="All fees, payments and reports use this currency." testId="pay-settings-general">
      <Field label="Currency"><Select value={cur} onValueChange={setCur} disabled={!can}>
        <SelectTrigger className="w-40" data-testid="pay-settings-currency"><SelectValue /></SelectTrigger>
        <SelectContent>{currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select></Field>
      <div className="space-y-2.5 mt-4">{NOTIFY.map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 text-sm text-slate-700"><Switch disabled={!can} checked={!!notify[k]} onCheckedChange={v => setNotify(n => ({ ...n, [k]: v }))} data-testid={`pay-notify-${k}`} />{l}</label>
      ))}</div>
      {can && <SaveBtn busy={busy} onClick={() => run({ currency: cur, notify }, 'Payment settings saved')} testId="pay-settings-general-save" />}
    </SettingsCard>
  );
};

const CancellationCard = ({ p, currency, can, save }) => {
  const c0 = p.cancellation;
  const [c, setC] = useState({ ...c0, flat: minorToInput(c0.flat_minor) });
  const [busy, run] = useSaver(save);
  const set = (k, v) => setC(x => ({ ...x, [k]: v }));
  return (
    <SettingsCard title="Cancellation fees" desc="By default, cancelled games pay nothing. Turn this on to pay officials when a game is cancelled at short notice." testId="pay-settings-cancellation">
      <label className="flex items-center gap-2 text-sm text-slate-700"><Switch disabled={!can} checked={!!c.enabled} onCheckedChange={v => set('enabled', v)} data-testid="pay-cancel-enabled" />Pay a cancellation fee</label>
      <div className={`grid grid-cols-3 gap-3 mt-3 ${c.enabled ? '' : 'opacity-40 pointer-events-none'}`}>
        <Field label="If cancelled within (hours)"><Input type="number" min={0} max={720} disabled={!can} value={c.within_hours} onChange={e => set('within_hours', e.target.value)} data-testid="pay-cancel-hours" /></Field>
        <Field label="Fee type"><Select value={c.mode} onValueChange={v => set('mode', v)} disabled={!can}>
          <SelectTrigger data-testid="pay-cancel-mode"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="percent">% of game fee</SelectItem><SelectItem value="flat">Flat amount</SelectItem></SelectContent>
        </Select></Field>
        {c.mode === 'flat'
          ? <Field label={`Amount (${currency})`}><Input type="number" min={0} step="0.01" disabled={!can} value={c.flat} onChange={e => set('flat', e.target.value)} data-testid="pay-cancel-flat" /></Field>
          : <Field label="Percent"><Input type="number" min={0} max={100} disabled={!can} value={c.percent} onChange={e => set('percent', e.target.value)} data-testid="pay-cancel-percent" /></Field>}
      </div>
      {can && <SaveBtn busy={busy} testId="pay-settings-cancellation-save"
        onClick={() => run({ cancellation: { enabled: c.enabled, within_hours: Number(c.within_hours) || 0, mode: c.mode, percent: Number(c.percent) || 0, flat: c.flat || 0 } }, 'Cancellation rule saved')} />}
    </SettingsCard>
  );
};

const BudgetsCard = ({ orgId, p, currency, competitions, can, save, tick }) => {
  const [vals, setVals] = useState(() => Object.fromEntries(competitions.map(c => [c.id, minorToInput(p.budgets[c.id])])));
  const [usage, setUsage] = useState({});
  const [busy, run] = useSaver(save);
  useEffect(() => {
    oapi('/officials/payments/reports/league_expense', { orgId }).then(r => setUsage(Object.fromEntries(r.sections[0].rows.map(x => [x.event_id, x])))).catch(() => {});
  }, [orgId, tick]);
  const tone = { over: 'text-red-600', expected_over: 'text-red-600', near: 'text-amber-600' };
  return (
    <SettingsCard title="Budgets" desc="Optional officiating budget per league or tournament. You'll see a warning on the dashboard when spend gets close." testId="pay-settings-budgets">
      <div className="space-y-2">{competitions.map(c => {
        const u = usage[c.id];
        return (
          <div key={c.id} className="flex flex-wrap items-center gap-3" data-testid={`budget-row-${c.id}`}>
            <div className="flex-1 min-w-[160px]"><div className="text-sm font-semibold text-slate-700">{c.name}</div>
              {u && <div className={`text-[11px] ${tone[u.warning] || 'text-slate-400'}`} data-testid={`budget-usage-${c.id}`}>
                Committed {fmtMoney(u.committed, currency)} · expected {fmtMoney(u.expected, currency)}{u.used_pct !== null ? ` · ${u.used_pct}% used` : ''}</div>}
            </div>
            <Input type="number" min={0} step="0.01" disabled={!can} value={vals[c.id] ?? ''} onChange={e => setVals(v => ({ ...v, [c.id]: e.target.value }))} placeholder="No budget" className="w-36 h-9 text-right" data-testid={`budget-input-${c.id}`} />
          </div>
        );
      })}
      {!competitions.length && <p className="text-sm text-slate-400">No leagues or tournaments yet.</p>}</div>
      {can && competitions.length > 0 && <SaveBtn busy={busy} onClick={() => run({ budgets: Object.fromEntries(competitions.map(c => [c.id, vals[c.id] || null])) }, 'Budgets saved')} testId="pay-settings-budgets-save" />}
    </SettingsCard>
  );
};

export const PaySettingsPanel = ({ orgId, meta, competitions, perms, tick, onSaved }) => {
  const can = perms.has('rates');
  const p = meta.payments;
  const save = async (body, msg) => {
    const r = await oapi('/officials/payments/settings', { method: 'PUT', orgId, body });
    onSaved(r);
    toast.success(msg);
  };
  return (
    <div className="space-y-4" data-testid="pay-settings">
      {!can && <p className="text-xs flex items-center gap-1.5 text-slate-500 bg-white border rounded-lg px-3 py-2"><Lock className="w-3.5 h-3.5" />View only. Ask the owner for "Manage rates" to change these.</p>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 [&>*]:min-w-0">
        <div className="space-y-4">
          <GeneralCard key={`g-${p.updated_at}`} p={p} currencies={meta.currencies} can={can} save={save} />
          <CancellationCard key={`c-${p.updated_at}`} p={p} currency={p.currency} can={can} save={save} />
        </div>
        <BudgetsCard key={`b-${p.updated_at}`} orgId={orgId} p={p} currency={p.currency} competitions={competitions} can={can} save={save} tick={tick} />
      </div>
    </div>
  );
};
